import * as Pages from "@/components/Pages";
import * as Paper from "@/components/PaperContainer";
import * as Companies from "@/models/companies";
import * as React from "react";

import { showErrorToast, showSuccessToast } from "turboui";
import { PageModule } from "@/routes/types";

import {
  getOverview,
  createDomain,
  createIdentity,
  assignSpace,
  unassignSpace,
  updateIdentity,
  EmailOverview,
  EmailIdentity,
} from "@/models/emailManagement";

export default { name: "CompanyEmailManagementPage", loader, Page } as PageModule;

/** "deliverability_hold" is a machine hold; say so in words rather than showing the raw enum. */
function statusLabel(identity: EmailIdentity) {
  return identity.status === "deliverability_hold" ? "auto-paused (placement)" : identity.status;
}

// Starting cap / step / target presets. A brand-new cold mailbox should crawl; an established one can
// climb faster. Admins can still hand-tune the numbers after picking a preset.
const RAMP_PRESETS: Record<string, { dailyCap: number; step: number; targetCap: number }> = {
  "Cautious (cold / brand-new)": { dailyCap: 3, step: 3, targetCap: 25 },
  Standard: { dailyCap: 5, step: 5, targetCap: 40 },
  "Faster (established domain)": { dailyCap: 10, step: 10, targetCap: 60 },
};

interface LoaderResult {
  company: Companies.Company;
  overview: EmailOverview;
}

async function loader(): Promise<LoaderResult> {
  const company = await Companies.getCompany({ includePermissions: true }).then((r: any) => r.company);
  if (!company.permissions?.isAdmin) throw new Response("Not Found", { status: 404 });
  const overview = await getOverview();
  return { company, overview };
}

function Page() {
  const { overview: initial } = Pages.useLoadedData() as LoaderResult;
  const [overview, setOverview] = React.useState<EmailOverview>(initial);

  const refresh = React.useCallback(async () => {
    try {
      setOverview(await getOverview());
    } catch {
      showErrorToast("Error", "Could not refresh email data");
    }
  }, []);

  return (
    <Pages.Page title={["Email"]}>
      <Paper.Root size="medium">
        <Paper.Body>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">Email</h1>
              <p className="text-sm text-content-dimmed mt-1 mb-6 max-w-2xl">
                Sender identities, their warmup ramp, and which teams send as each one. Agents never pick a sender: the
                platform assigns a consistent identity per recipient and drips within its daily limit.
              </p>
            </div>
            <a
              href="inbox"
              className="shrink-0 px-3 py-1.5 text-sm border border-surface-outline rounded font-semibold"
            >
              Open Inbox
            </a>
          </div>

          <Summary overview={overview} />
          <Identities overview={overview} refresh={refresh} />
          <Domains overview={overview} refresh={refresh} />
          <AddIdentity overview={overview} refresh={refresh} />
        </Paper.Body>
      </Paper.Root>
    </Pages.Page>
  );
}

function Summary({ overview }: { overview: EmailOverview }) {
  const s = overview.summary;
  return (
    <div className="flex gap-6 border border-surface-outline rounded p-4 mb-6">
      <Stat label="Sent today" value={`${s.sentToday} / ${s.capToday}`} />
      <Stat label="Queued" value={String(s.backlog)} />
      <Stat label="Identities" value={String(overview.identities.length)} />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-content-dimmed">{label}</div>
    </div>
  );
}

function Identities({ overview, refresh }: { overview: EmailOverview; refresh: () => Promise<void> }) {
  if (overview.identities.length === 0) {
    return <div className="text-sm text-content-dimmed mb-6">No sender identities yet. Add one below.</div>;
  }
  return (
    <div className="mb-8">
      <h2 className="font-bold mb-2">Sender identities</h2>
      {overview.identities.map((i) => (
        <IdentityCard key={i.id} identity={i} overview={overview} refresh={refresh} />
      ))}
    </div>
  );
}

function IdentityCard({
  identity,
  overview,
  refresh,
}: {
  identity: EmailIdentity;
  overview: EmailOverview;
  refresh: () => Promise<void>;
}) {
  const paused = identity.status === "paused";
  // A machine hold is deliberately NOT a pause: the monitor owns it and releases it, so the
  // manual Pause/Resume button must not be able to fight it.
  const held = identity.status === "deliverability_hold";
  const pct = identity.dailyCap ? Math.min(100, (identity.sentToday / identity.dailyCap) * 100) : 0;

  const [editingRamp, setEditingRamp] = React.useState(false);
  const [target, setTarget] = React.useState(identity.targetCap);
  const [step, setStep] = React.useState(identity.step);

  const assignedIds = identity.spaces.map((s) => s.id);
  const unassigned = overview.spaces.filter((s) => !assignedIds.includes(s.id));

  const act = async (fn: () => Promise<any>, ok: string) => {
    try {
      await fn();
      showSuccessToast("Done", ok);
      await refresh();
    } catch {
      showErrorToast("Error", "That action failed");
    }
  };

  return (
    <div className="border border-surface-outline rounded p-4 mb-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-semibold">
            {identity.displayName} <span className="text-content-dimmed font-normal">· {identity.address}</span>
          </div>
          <div className="text-xs text-content-dimmed">
            {identity.roleTitle || "—"} · {identity.lane} lane · {statusLabel(identity)}
            {!identity.mailboxConfigured && " · mailbox password not set"}
          </div>
        </div>
        <button
          className="text-xs border border-surface-outline rounded px-2 py-1 hover:bg-surface-accent disabled:opacity-50"
          disabled={held}
          title={held ? "Parked automatically on inbox placement. It resumes itself once placement recovers." : undefined}
          onClick={() => act(() => updateIdentity({ id: identity.id, status: paused ? "warming" : "paused" }), paused ? "Resumed" : "Paused")}
        >
          {paused ? "Resume" : "Pause"}
        </button>
      </div>

      {held ? (
        <div className="mt-3 border border-callout-warning-content/30 bg-callout-warning-bg rounded px-3 py-2 text-xs">
          <span className="font-semibold">Parked automatically — inbox placement.</span>{" "}
          {identity.deliverabilityHoldReason}{" "}
          <span className="text-content-dimmed">
            This was not a manual pause. It resumes on its own once placement recovers.
          </span>
        </div>
      ) : null}

      {identity.monitored && identity.placementRate != null ? (
        <div className="mt-2 text-xs text-content-dimmed">
          Inbox placement {identity.placementRate}%
          {identity.placementFloor != null
            ? ` (floor ${identity.placementFloor}%)`
            : " (no floor yet — still inside the warmup grace period)"}
          {identity.placementCheckedAt ? ` · checked ${new Date(identity.placementCheckedAt).toLocaleString()}` : ""}
        </div>
      ) : null}

      <div className="mt-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span>
            Warmup: {identity.sentToday} / {identity.dailyCap} today (target {identity.targetCap})
          </span>
          <span className="text-content-dimmed">day {identity.dayIndex}</span>
        </div>
        <div className="h-2 bg-surface-dimmed rounded overflow-hidden">
          <div className="h-full bg-accent-1" style={{ width: `${pct}%` }} />
        </div>

        <div className="mt-2 text-xs">
          {editingRamp ? (
            <div className="flex items-center gap-2 flex-wrap">
              <label>Target/day</label>
              <input
                type="number"
                className="w-16 border border-surface-outline rounded px-1 py-0.5"
                value={target}
                onChange={(e) => setTarget(parseInt(e.target.value) || 0)}
              />
              <label>Step/day</label>
              <input
                type="number"
                className="w-14 border border-surface-outline rounded px-1 py-0.5"
                value={step}
                onChange={(e) => setStep(parseInt(e.target.value) || 0)}
              />
              <button
                className="border border-surface-outline rounded px-2 py-0.5 hover:bg-surface-accent"
                onClick={() =>
                  act(async () => {
                    await updateIdentity({ id: identity.id, targetCap: target, step });
                    setEditingRamp(false);
                  }, "Ramp updated")
                }
              >
                Save
              </button>
              <button className="text-content-dimmed" onClick={() => setEditingRamp(false)}>
                Cancel
              </button>
            </div>
          ) : (
            <button
              className="text-content-dimmed underline hover:text-content-base"
              onClick={() => setEditingRamp(true)}
            >
              Climbs +{identity.step}/day toward {identity.targetCap} · edit target
            </button>
          )}
        </div>
      </div>

      <div className="mt-3">
        <div className="text-xs text-content-dimmed mb-1">Sends for these teams:</div>
        <div className="flex flex-wrap gap-2 items-center">
          {identity.spaces.map((s) => (
            <span key={s.id} className="text-xs bg-surface-dimmed rounded px-2 py-1 flex items-center gap-1">
              {s.name}
              <button
                className="text-content-dimmed hover:text-content-error"
                onClick={() => act(() => unassignSpace({ identityId: identity.id, spaceId: s.id }), "Unassigned")}
              >
                ×
              </button>
            </span>
          ))}
          {unassigned.length > 0 && (
            <select
              className="text-xs border border-surface-outline rounded px-2 py-1 bg-surface-base"
              value=""
              onChange={(e) => {
                const spaceId = e.target.value;
                if (spaceId) act(() => assignSpace({ identityId: identity.id, spaceId }), "Assigned");
              }}
            >
              <option value="">+ assign team…</option>
              {unassigned.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
    </div>
  );
}

function Domains({ overview, refresh }: { overview: EmailOverview; refresh: () => Promise<void> }) {
  const [domain, setDomain] = React.useState("");
  const [kind, setKind] = React.useState("primary");

  const submit = async () => {
    if (!domain.trim()) return;
    try {
      await createDomain({ domain: domain.trim(), kind });
      setDomain("");
      showSuccessToast("Done", "Domain added");
      await refresh();
    } catch {
      showErrorToast("Error", "Could not add domain");
    }
  };

  return (
    <div className="mb-8">
      <h2 className="font-bold mb-2">Sending domains</h2>
      <div className="flex flex-wrap gap-2 mb-3">
        {overview.domains.map((d) => (
          <span key={d.id} className="text-xs bg-surface-dimmed rounded px-2 py-1">
            {d.domain} · {d.kind}
          </span>
        ))}
        {overview.domains.length === 0 && <span className="text-xs text-content-dimmed">None yet.</span>}
      </div>
      <div className="flex gap-2 items-center">
        <input
          className="border border-surface-outline rounded px-2 py-1 text-sm"
          placeholder="shotbreak.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
        />
        <select
          className="border border-surface-outline rounded px-2 py-1 text-sm bg-surface-base"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        >
          <option value="primary">primary (warm)</option>
          <option value="cold">cold (prospecting)</option>
        </select>
        <button className="text-sm border border-surface-outline rounded px-3 py-1 hover:bg-surface-accent" onClick={submit}>
          Add domain
        </button>
      </div>
    </div>
  );
}

function AddIdentity({ overview, refresh }: { overview: EmailOverview; refresh: () => Promise<void> }) {
  const [f, setF] = React.useState({
    domainId: "",
    address: "",
    displayName: "",
    roleTitle: "",
    lane: "warm",
    signature: "",
    smtpPasswordRef: "",
  });

  const set = (k: string, v: string) => setF((prev) => ({ ...prev, [k]: v }));
  const [ramp, setRamp] = React.useState({ dailyCap: 5, step: 5, targetCap: 40 });

  const submit = async () => {
    if (!f.domainId || !f.address.trim() || !f.displayName.trim()) {
      showErrorToast("Missing fields", "Pick a domain and enter an address and display name");
      return;
    }
    try {
      await createIdentity({
        domainId: f.domainId,
        address: f.address.trim(),
        displayName: f.displayName.trim(),
        roleTitle: f.roleTitle.trim() || undefined,
        lane: f.lane,
        signature: f.signature.trim() || undefined,
        smtpPasswordRef: f.smtpPasswordRef.trim() || undefined,
        dailyCap: ramp.dailyCap,
        step: ramp.step,
        targetCap: ramp.targetCap,
      });
      setF({ domainId: "", address: "", displayName: "", roleTitle: "", lane: "warm", signature: "", smtpPasswordRef: "" });
      setRamp({ dailyCap: 5, step: 5, targetCap: 40 });
      showSuccessToast("Done", "Identity created");
      await refresh();
    } catch {
      showErrorToast("Error", "Could not create identity");
    }
  };

  if (overview.domains.length === 0) {
    return <div className="text-sm text-content-dimmed">Add a sending domain first, then you can create identities.</div>;
  }

  return (
    <div className="border border-surface-outline rounded p-4">
      <h2 className="font-bold mb-3">Add a sender identity</h2>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Domain">
          <select className="w-full border border-surface-outline rounded px-2 py-1 text-sm bg-surface-base" value={f.domainId} onChange={(e) => set("domainId", e.target.value)}>
            <option value="">Select…</option>
            {overview.domains.map((d) => (
              <option key={d.id} value={d.id}>
                {d.domain}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Lane">
          <select className="w-full border border-surface-outline rounded px-2 py-1 text-sm bg-surface-base" value={f.lane} onChange={(e) => set("lane", e.target.value)}>
            <option value="warm">warm</option>
            <option value="cold">cold</option>
          </select>
        </Field>
        <Field label="Email address">
          <Input value={f.address} onChange={(v) => set("address", v)} placeholder="maya@shotbreak.com" />
        </Field>
        <Field label="Display name">
          <Input value={f.displayName} onChange={(v) => set("displayName", v)} placeholder="Maya Chen" />
        </Field>
        <Field label="Role / title">
          <Input value={f.roleTitle} onChange={(v) => set("roleTitle", v)} placeholder="Customer Success, ShotBreak" />
        </Field>
        <Field label="Mailbox password secret name">
          <Input value={f.smtpPasswordRef} onChange={(v) => set("smtpPasswordRef", v)} placeholder="SHOTBREAK_MAYA_SMTP_PW" />
        </Field>
      </div>

      <div className="mt-4 border-t border-surface-outline pt-3">
        <div className="text-xs font-semibold mb-2">Warmup ramp</div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            className="border border-surface-outline rounded px-2 py-1 text-sm bg-surface-base"
            value=""
            onChange={(e) => {
              const p = RAMP_PRESETS[e.target.value];
              if (p) setRamp(p);
            }}
          >
            <option value="">Preset…</option>
            {Object.keys(RAMP_PRESETS).map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
          <label className="text-xs text-content-dimmed">Start/day</label>
          <input
            type="number"
            className="w-16 border border-surface-outline rounded px-2 py-1 text-sm"
            value={ramp.dailyCap}
            onChange={(e) => setRamp((r) => ({ ...r, dailyCap: parseInt(e.target.value) || 0 }))}
          />
          <label className="text-xs text-content-dimmed">Step/day</label>
          <input
            type="number"
            className="w-16 border border-surface-outline rounded px-2 py-1 text-sm"
            value={ramp.step}
            onChange={(e) => setRamp((r) => ({ ...r, step: parseInt(e.target.value) || 0 }))}
          />
          <label className="text-xs text-content-dimmed">Target/day</label>
          <input
            type="number"
            className="w-16 border border-surface-outline rounded px-2 py-1 text-sm"
            value={ramp.targetCap}
            onChange={(e) => setRamp((r) => ({ ...r, targetCap: parseInt(e.target.value) || 0 }))}
          />
        </div>
        <div className="text-xs text-content-dimmed mt-1">
          Starts at {ramp.dailyCap}/day, climbs +{ramp.step}/day on clean days, up to {ramp.targetCap}/day.
        </div>
      </div>

      <div className="mt-3">
        <div className="text-xs text-content-dimmed mb-1">Signature (left-aligned, appended to every send)</div>
        <textarea
          className="w-full border border-surface-outline rounded px-2 py-1 text-sm h-20"
          value={f.signature}
          onChange={(e) => set("signature", e.target.value)}
          placeholder={"Maya Chen\nCustomer Success, ShotBreak\nshotbreak.com"}
        />
      </div>
      <button className="mt-3 text-sm border border-surface-outline rounded px-3 py-1 hover:bg-surface-accent" onClick={submit}>
        Create identity
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-content-dimmed mb-1">{label}</div>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <input
      className="w-full border border-surface-outline rounded px-2 py-1 text-sm"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
